import React from "react";
import { baseAsset } from "@/lib/utils";

const fmt = (n, opts = {}) => {
    if (n === null || n === undefined || Number.isNaN(n)) return "--";
    return Number(n).toLocaleString("en-US", { maximumFractionDigits: 4, ...opts });
};

const Card = ({ label, value, suffix, accent = "#E6EDF6", testId, mono = true }) => (
    <div
        className="panel p-4 flex flex-col gap-1"
        data-testid={testId}
        style={{ minHeight: 96 }}
    >
        <div className="text-[10px] uppercase tracking-[0.14em] text-[#A7B4C6]">{label}</div>
        <div className={`${mono ? "font-mono" : ""} text-2xl`} style={{ color: accent }}>
            {value}
            {suffix && (
                <span className="text-xs ml-1 text-[#6F7D92] font-mono">{suffix}</span>
            )}
        </div>
    </div>
);

export const StatsBento = ({ status, pnl }) => {
    const realizedGross = status?.realized_pnl_gross ?? pnl?.realized_pnl_total ?? status?.realized_pnl ?? 0;
    const fees = status?.fees_usdt ?? 0;
    const realizedNet = status?.realized_pnl ?? (realizedGross - fees);
    const realized24h = pnl?.realized_pnl_24h ?? 0;
    const unrealized = status?.unrealized_pnl ?? 0;
    const cycles = pnl?.cycles_completed ?? status?.completed_cycles ?? 0;
    const activeBuys = status?.active_buys ?? 0;
    const activeSells = status?.active_sells ?? 0;
    const btcInv = status?.balances?.btc_free ?? 0;
    const usdtFree = status?.balances?.usdt_free ?? 0;
    const money = (n) => `${n >= 0 ? "+" : ""}$${fmt(n, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    return (
        <div className="grid grid-cols-2 lg:grid-cols-2 gap-3">
            <div
                className="panel p-4 flex flex-col gap-1 col-span-2"
                data-testid="stats-net-pnl"
                style={{ minHeight: 96 }}
            >
                <div className="text-[10px] uppercase tracking-[0.14em] text-[#A7B4C6]">
                    Net PnL (after fees) · matches exchange
                </div>
                <div className="font-mono text-2xl" style={{ color: realizedNet >= 0 ? "#2BE38B" : "#FF5C6C" }}
                     data-testid="stats-net-pnl-value">
                    {money(realizedNet)}
                </div>
                <div className="text-[11px] font-mono text-[#6F7D92] mt-0.5" data-testid="stats-pnl-breakdown">
                    Gross <span className="text-[#A7B4C6]">{money(realizedGross)}</span>
                    {"  −  "}Fees <span className="text-[#FF8A5C]">${fmt(fees, { minimumFractionDigits: 2, maximumFractionDigits: 4 })}</span>
                    {"  =  "}Net <span style={{ color: realizedNet >= 0 ? "#2BE38B" : "#FF5C6C" }}>{money(realizedNet)}</span>
                </div>
            </div>
            <Card
                label="Realized PnL (24h)"
                value={`${realized24h >= 0 ? "+" : ""}$${fmt(realized24h, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                accent={realized24h >= 0 ? "#2BE38B" : "#FF5C6C"}
                testId="stats-realized-pnl-24h"
            />
            <Card
                label="Fees Paid (total)"
                value={`$${fmt(fees, { minimumFractionDigits: 2, maximumFractionDigits: 4 })}`}
                accent="#FF8A5C"
                testId="stats-fees-paid"
            />
            <Card
                label="Unrealized PnL (held)"
                value={`${unrealized >= 0 ? "+" : ""}$${fmt(unrealized, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                accent={unrealized >= 0 ? "#2BE38B" : "#FF5C6C"}
                testId="stats-unrealized-pnl"
            />
            <Card
                label="Cycles Completed"
                value={fmt(cycles, { maximumFractionDigits: 0 })}
                testId="stats-cycles-completed"
            />
            <Card
                label="Active BUYs / SELLs"
                value={`${activeBuys} / ${activeSells}`}
                accent="#2EE6D6"
                testId="stats-active-orders"
            />
            <Card
                label={`${baseAsset(status?.config?.symbol)} Inventory (free)`}
                value={fmt(btcInv, { maximumFractionDigits: 6 })}
                suffix={baseAsset(status?.config?.symbol)}
                testId="stats-btc-inventory"
            />
            <Card
                label="USDT (free)"
                value={`$${fmt(usdtFree, { maximumFractionDigits: 2 })}`}
                testId="stats-usdt-free"
            />
        </div>
    );
};

export default StatsBento;
